import React, { useEffect, useMemo, useState } from 'react';
import { z, ChoiceGroupItem, Form, SubmitHandler, useForm, zodResolver } from '@fiverr-private/forms';
import pathfinder from '@fiverr-private/pathfinder';
import { Stack } from '@fiverr-private/layout_components';
import { getContext } from '@fiverr-private/fiverr_context';
import { Text, Typography } from '@fiverr-private/typography';
import { triggerSignIn } from '@fiverr-private/identification_triggers';
import { Drawer, DrawerBody, DrawerContent } from '@fiverr-private/overlay';
import { raiseToast } from '@fiverr-private/toast';
import { logError } from '../../../lib/logError';
import { useBreakpointMd } from '../../../utils/breakpoints';
import { appTranslate } from '../../i18n';
import {
    getSubscriptionFormDataCookie,
    removeSubscriptionDrawerIsOpenCookie,
    removeSubscriptionFormDataCookie,
    setSubscriptionDrawerIsOpenCookie,
    setSubscriptionFormDataCookie,
} from '../../../lib/cookies';
import { FormInput } from '../shared/FormInput';
import { FormChoiceGroup } from '../shared/FormChoiceGroup';
import { BillingCycle } from '../../../type';
import { createMicrosoft365Subscription } from '../../../api';
import { useResellingProgramsContext } from '../../context';
import { reportPageViewResellingPlansSubscriptionDrawer } from '../../../events/bianka';
import { restrictDecimalHandler } from '../../helpers';
import { useIsDiscountPage } from '../../hooks';
import { SubscriptionHeader } from './SubscriptionHeader';
import { SubscriptionFooter } from './SubscriptionFooter';
import { SubscriptionSummary } from './SubscriptionSummary';
import { ReachedMaxLicensesAlert } from './ReachedMaxLicensesAlert';
import { createSchema, getSubscriptionOptions, FormSchema } from './schema';
import { SubscriptionEligibilityAlert } from './SubscriptionEligibilityAlert';
import { useSubscriptionSummarizePrice } from './hooks';

interface SubscriptionDrawerProps {
    isOpenDrawer: boolean;
    onCloseDrawer: () => void;
}

export const SubscriptionDrawer = ({ isOpenDrawer, onCloseDrawer }: SubscriptionDrawerProps) => {
    const isMdBreakpoint = useBreakpointMd();
    const isDiscountPage = useIsDiscountPage();

    const [schema, setSchema] = useState<z.ZodSchema | null>(null);

    const {
        sellerName,
        subscriptionDetailsByTier,
        resellingPrograms: { id: vendorId, slug: vendorName, sellerId },
        selectedCountryState: { selectedCountry: country },
        billingCycleState: { billingCycle },
        selectedTierState: { selectedTier: subscriptionTier },
    } = useResellingProgramsContext();

    const { minQuantity = 0, maxQuantity = 0, totalQuantity = 0 } = subscriptionDetailsByTier.get(billingCycle) ?? {};
    const isMaximumUsersExceedError = maxQuantity === 0;
    const isEligible = sellerId !== getContext().userId;

    useEffect(() => {
        if (isOpenDrawer) {
            setSchema(() => createSchema({ maxQuantity, minQuantity, totalQuantity }));
        }
    }, [isOpenDrawer, maxQuantity, minQuantity, totalQuantity]);

    const defaultValues = useMemo(
        () =>
            getSubscriptionFormDataCookie(sellerName) || {
                quantity: minQuantity,
                billingCycle: !isDiscountPage ? billingCycle : BillingCycle.YEARLY,
                subscriptionTier,
            },
        [sellerName, minQuantity, billingCycle, subscriptionTier, isDiscountPage]
    );

    const methods = useForm<FormSchema>({
        resolver: schema ? zodResolver(schema) : undefined,
        mode: 'all',
        defaultValues,
    });

    const formValues = methods.watch();
    const pricingDetails = subscriptionDetailsByTier.get(formValues.billingCycle);

    const { singularPlanPrice, totalPrice } = useSubscriptionSummarizePrice({
        pricingDetails,
        licensesQuantity: formValues.quantity,
    });

    const handleCleanAll = () => {
        removeSubscriptionDrawerIsOpenCookie(sellerName);
        removeSubscriptionFormDataCookie(sellerName);
    };

    const handleSubmit: SubmitHandler<FormSchema> = async (formData: FormSchema) => {
        const { userId } = getContext();

        try {
            if (!userId) {
                setSubscriptionDrawerIsOpenCookie(sellerName);
                setSubscriptionFormDataCookie(sellerName, formData);

                await triggerSignIn({
                    modalOptions: {
                        triggerType: 'blocked_action',
                        blockedActionType: 'continue_to_checkout',
                        triggerId: 'reselling_continue',
                        triggerCopy: appTranslate<string>('subscription_drawer.continue'),
                        triggerPlacement: 'reselling_listing',
                        triggerComments: 'Reselling listing continue to checkout',
                        redirectUrl: pathfinder('reselling_listings', { seller_name: sellerName }),
                        skipQuestionnaire: true,
                    },
                });
            } else {
                handleCleanAll();

                if (!pricingDetails || !pricingDetails.productId) {
                    throw new Error(`Invalid pricingDetails: ${JSON.stringify(pricingDetails)}`);
                }

                const paymentSessionId = await createMicrosoft365Subscription({
                    programId: vendorId,
                    productId: pricingDetails.productId,
                    country,
                    billingTerm: pricingDetails.billingTerm,
                    billingCycle: pricingDetails.billingCycle,
                    sellerId,
                    quantity: formData.quantity,
                    isDiscountApplied: isDiscountPage,
                });

                removeSubscriptionDrawerIsOpenCookie(sellerName);
                removeSubscriptionFormDataCookie(sellerName);

                window.location.assign(
                    pathfinder('payments_page', {}, { query: { paymentSessionID: paymentSessionId } })
                );
            }
        } catch (error) {
            logError(error, `Failed to create Microsoft 365 Subscription or trigger sign in`);
            raiseToast({ type: 'error', location: 'top', message: appTranslate('common.errors.default') });
        }
    };

    const handleCloseDrawer = () => {
        methods.reset();
        handleCleanAll();
        onCloseDrawer();
    };

    useEffect(() => {
        methods.reset(defaultValues);
    }, [methods, defaultValues]);

    useEffect(() => {
        if (isOpenDrawer) {
            reportPageViewResellingPlansSubscriptionDrawer({
                sellerId,
                buyerId: getContext().userId,
            });
        }
    });

    return (
        <Drawer isOpen={isOpenDrawer} onClose={handleCloseDrawer}>
            <Form methods={methods} onSubmit={handleSubmit}>
                <DrawerContent
                    position={isMdBreakpoint ? 'right' : 'bottom'}
                    closeOnEscapeKey
                    isFullScreen={!isMdBreakpoint}
                >
                    <SubscriptionHeader />
                    <DrawerBody>
                        <Stack direction="column" gap="8">
                            {!isEligible && <SubscriptionEligibilityAlert />}
                            {isMaximumUsersExceedError && <ReachedMaxLicensesAlert totalQuantity={totalQuantity} />}

                            <FormInput
                                min={minQuantity}
                                max={maxQuantity}
                                step={1}
                                type="number"
                                label={appTranslate('subscription_drawer.fields.quantity.label')}
                                placeholder={appTranslate<string>('subscription_drawer.fields.quantity.placeholder')}
                                disabled={methods.formState.isSubmitting}
                                name="quantity"
                                onKeyDown={restrictDecimalHandler}
                                onPaste={(e) => e.preventDefault()}
                            />

                            {!isDiscountPage && (
                                <FormChoiceGroup<BillingCycle>
                                    name="billingCycle"
                                    label={appTranslate('subscription_drawer.fields.billing_cycle.label')}
                                >
                                    {getSubscriptionOptions(subscriptionDetailsByTier).map(
                                        ({ value, label, price }) => (
                                            <ChoiceGroupItem key={value} value={value}>
                                                <Text fontWeight="semibold" size="b_md" marginBottom="0.5">
                                                    {label}
                                                </Text>
                                                <Typography color="grey_1000" size="b_md">
                                                    {price}
                                                </Typography>
                                            </ChoiceGroupItem>
                                        )
                                    )}
                                </FormChoiceGroup>
                            )}

                            <SubscriptionSummary
                                billingCycle={formValues.billingCycle}
                                totalPrice={totalPrice}
                                singularPlanPrice={singularPlanPrice}
                                subscriptionPlanName={`${appTranslate(`common.vendors.${vendorName}`)} ${appTranslate(
                                    `common.subscription_tiers.${subscriptionTier}`
                                )}`}
                                usersCount={Math.abs(formValues.quantity) || 1}
                            />
                        </Stack>
                    </DrawerBody>
                    <SubscriptionFooter
                        totalPrice={totalPrice}
                        onSubmit={methods.handleSubmit(handleSubmit)}
                        loading={methods.formState.isSubmitting}
                        disabled={
                            methods.formState.isSubmitting || !totalPrice || isMaximumUsersExceedError || !isEligible
                        }
                    />
                </DrawerContent>
            </Form>
        </Drawer>
    );
};
